import { useState } from "react";
import { Box, Chip, IconButton, MenuItem, TextField, Typography } from "@mui/material";
import { Edit, Save, Close } from "@mui/icons-material";
import { useUpdateRisk } from "@/api/hooks/extraction";
import {
  RISK_SEVERITIES,
  RISK_SEVERITY_LABELS,
  RISK_STATUSES,
  RISK_STATUS_LABELS,
} from "@/api/labels";
import type { Risk, RiskSeverity, RiskStatus } from "@/api/types";

const SEVERITY_COLOR: Record<
  RiskSeverity,
  "default" | "info" | "warning" | "error"
> = {
  low: "default",
  medium: "info",
  high: "warning",
  critical: "error",
};

const STATUS_COLOR: Record<
  RiskStatus,
  "info" | "warning" | "success" | "default"
> = {
  open: "info",
  mitigated: "warning",
  accepted: "warning",
  closed: "success",
};

export default function RiskRow({
  risk,
  transcriptId,
}: {
  risk: Risk;
  transcriptId: string;
}) {
  const [editing, setEditing] = useState(false);
  const [severity, setSeverity] = useState<RiskSeverity>(risk.severity);
  const [status, setStatus] = useState<RiskStatus>(risk.status);
  const updateMutation = useUpdateRisk(transcriptId);

  const save = async () => {
    await updateMutation.mutateAsync({
      riskId: risk.id,
      payload: { severity, status },
    });
    setEditing(false);
  };

  const cancel = () => {
    setSeverity(risk.severity);
    setStatus(risk.status);
    setEditing(false);
  };

  if (!editing) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 1.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <Chip
          size="small"
          label={RISK_SEVERITY_LABELS[risk.severity]}
          color={SEVERITY_COLOR[risk.severity]}
        />
        <Chip
          size="small"
          variant="outlined"
          label={RISK_STATUS_LABELS[risk.status]}
          color={STATUS_COLOR[risk.status]}
        />
        <Typography sx={{ flex: 1 }}>{risk.description}</Typography>
        <IconButton
          size="small"
          onClick={() => setEditing(true)}
          aria-label="Edit risk"
        >
          <Edit fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        border: "1px solid",
        borderColor: "primary.main",
        borderRadius: 1,
      }}
    >
      <TextField
        size="small"
        select
        label="Severity"
        value={severity}
        onChange={(e) => setSeverity(e.target.value as RiskSeverity)}
        sx={{ width: 140 }}
      >
        {RISK_SEVERITIES.map((s) => (
          <MenuItem key={s} value={s}>
            {RISK_SEVERITY_LABELS[s]}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        select
        label="Status"
        value={status}
        onChange={(e) => setStatus(e.target.value as RiskStatus)}
        sx={{ width: 140 }}
      >
        {RISK_STATUSES.map((s) => (
          <MenuItem key={s} value={s}>
            {RISK_STATUS_LABELS[s]}
          </MenuItem>
        ))}
      </TextField>
      <Typography sx={{ flex: 1 }}>{risk.description}</Typography>
      <IconButton
        size="small"
        onClick={save}
        disabled={updateMutation.isPending}
        aria-label="Save"
      >
        <Save fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={cancel}
        disabled={updateMutation.isPending}
        aria-label="Cancel"
      >
        <Close fontSize="small" />
      </IconButton>
    </Box>
  );
}
